import { useEffect, useMemo } from "react";
import Select from "react-select";
import { useParams } from "react-router-dom";
import ModalGroupingTable from "../components/ModalGroupingTable";
import CostAllocation from "../components/CostAllocation.js";
import { useLazyGetDropDownsForProductsAndServicesQuery } from "../../../../../../app/services/productAndService/productAndServiceApi";
import { useLazyGetTVOCorFormaldehydeEmissionListQuery } from "../../../../../../app/services/selfEvalutionReviewService/selfEvalutionReviewServiceApi";
import {
  useLazyGetCertificateGroupingDetailsForTVOCByIdQuery,
  useLazyGetProductDetailsListByProductInformationIdQuery,
} from "../../../../../../app/services/CertificateGrouping/certificateGroupingApi";
import { decryptAESID } from "../../../../../../utils/Encryption";

const TICK_OPTIONS = [
  { label: "1 Tick", value: 1 },
  { label: "2 Ticks", value: 2 },
  { label: "3 Ticks", value: 3 },
  { label: "4 Ticks", value: 4 },
];

const toOptions = (items = [], dropdownName) =>
  items
    .filter((item) => item.dropdownName === dropdownName)
    .map((item) => ({ value: item.id, label: item.name }));

const findOption = (options, value) =>
  options.find((option) => option.value === value) ?? null;

const splitCertificateNumber = (certificateNumber) => {
  const value = String(certificateNumber ?? "");
  const slashIndex = value.lastIndexOf("/");
  if (slashIndex === -1) return { base: value, suffix: 0 };
  return {
    base: value.slice(0, slashIndex),
    suffix: Number(value.slice(slashIndex + 1)) || 0,
  };
};

const getCertificateLabel = ({ applicationNumber, certPrefix }) =>
  certPrefix ? `${applicationNumber}/${certPrefix}` : applicationNumber;

const getMinRatingByLabel = (products) => {
  const minRatings = new Map();
  products.forEach((product) => {
    if (!product?.applicationNumber) return;
    const label = getCertificateLabel(product);
    const { rating } = product;
    const parsed =
      rating === undefined || rating === null || rating === "" || Number.isNaN(Number(rating))
        ? null
        : Number(rating);
    const current = minRatings.get(label) ?? null;
    minRatings.set(label, current === null ? parsed : parsed === null ? current : Math.min(current, parsed));
  });
  return minRatings;
};

const getEmissionValue = (emissions, flag, tickRating) =>
  emissions.find((item) => item[flag] === true && Number(item.tickRating) === Number(tickRating))
    ?.actualValue ?? "";

const buildCostAllocationRows = (products, allocations, itemCodeOptions, emissions) => {
  const labels = [...new Set(products.map(getCertificateLabel))];
  const minRatings = getMinRatingByLabel(products);
  const hasSavedAllocations = allocations.length > 0;

  return labels.map((label) => {
    let value = null;
    let tickValue;

    if (hasSavedAllocations) {
      const { base, suffix } = splitCertificateNumber(label);
      const allocation = allocations.find((item) => {
        const current = splitCertificateNumber(item.certificateNumber);
        return current.base === base && current.suffix === suffix;
      });
      value = allocation ? findOption(itemCodeOptions, allocation.itemCode) : null;
      tickValue = allocation ? findOption(TICK_OPTIONS, allocation.tickRating) : null;
    } else {
      tickValue = findOption(TICK_OPTIONS, minRatings.get(label) ?? null);
    }

    return {
      label,
      value,
      tickValue,
      tvocEmission: getEmissionValue(emissions, "isTVOC", tickValue?.value),
      formaldehydeEmission: getEmissionValue(emissions, "isFormul", tickValue?.value),
    };
  });
};

const ReadOnlyTextarea = ({ label, value }) => (
  <div className="mt-2">
    <label className="form-label">{label}</label>
    <textarea className="form-control mt-1" rows="4" value={value} disabled />
  </div>
);

const ReadOnlySelectRow = ({ label, value, options, placeholder, className = "" }) => (
  <div className={`d-flex align-items-center ${className}`}>
    <label className="form-label mb-0 w-20">{label}</label>
    <div style={{ flex: 1 }}>
      <Select
        className="custom-checkbox-select"
        value={value}
        options={options}
        placeholder={placeholder}
        isDisabled
      />
    </div>
  </div>
);

const TVOCCertificate = () => {
  const { id } = useParams();

  const [loadDropdowns, { data: dropdownData }] = useLazyGetDropDownsForProductsAndServicesQuery();
  const [loadEmissions, { data: emissionData }] = useLazyGetTVOCorFormaldehydeEmissionListQuery();
  const [loadProducts, { data: productData }] = useLazyGetProductDetailsListByProductInformationIdQuery();
  const [loadGroupingDetails, { data: groupingData }] = useLazyGetCertificateGroupingDetailsForTVOCByIdQuery();

  const applicationId = useMemo(() => decryptAESID(id, false), [id]);

  useEffect(() => {
    document.querySelector(".center-container")?.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!(applicationId > 0)) return;
    loadDropdowns();
    loadEmissions();
    loadProducts(applicationId);
    loadGroupingDetails(applicationId);
  }, [applicationId, loadDropdowns, loadEmissions, loadProducts, loadGroupingDetails]);

  const products = useMemo(
    () => (Array.isArray(productData) ? productData : productData?.data ?? []),
    [productData]
  );
  const emissions = useMemo(() => (Array.isArray(emissionData) ? emissionData : []), [emissionData]);
  const approverOptions = useMemo(() => toOptions(dropdownData, "Approver List"), [dropdownData]);
  const itemCodeOptions = useMemo(() => toOptions(dropdownData, "Items Codes"), [dropdownData]);

  const costAllocationRows = useMemo(
    () =>
      buildCostAllocationRows(
        products,
        groupingData?.getCertificateAndCostAllocationForTVOCList ?? [],
        itemCodeOptions,
        emissions
      ),
    [products, groupingData, itemCodeOptions, emissions]
  );

  const peerReviewer = findOption(approverOptions, groupingData?.peerReviewerId);
  const approver = findOption(approverOptions, groupingData?.approverId);
  const caseOfficerComment = groupingData?.commentFromCaseOfficer ?? "";

  return (
    <div className="certificate-grouping-list-page">
      <div className="crud-requirement-list">
        <div className="corporate-member-list">
          <div className="table-title mt-2">
            <h3>Model Grouping</h3>
          </div>
          <ModalGroupingTable type="TVOC" data={products} />

          <div className="table-title mt-4">
            <h3>Certificate and Cost Allocation</h3>
          </div>
          <CostAllocation
            type="TVOC"
            rows={costAllocationRows}
            itemCodeOptions={itemCodeOptions}
            tickOptions={TICK_OPTIONS}
          />

          <div className="table-title mt-5 mb-4">
            <h3>Internal Notes</h3>
          </div>
          <ReadOnlyTextarea label="Comments from Case Officer" value={caseOfficerComment} />

          <div className="table-title mt-5 mb-4">
            <h3>Assign to</h3>
          </div>
          <div className="dropdown-select-wrapper">
            <ReadOnlySelectRow
              label="Peer Reviewer"
              value={peerReviewer}
              options={approverOptions}
              placeholder="Select Peer Reviewer"
              className="mb-4"
            />
            <ReadOnlySelectRow
              label="Approver"
              value={approver}
              options={approverOptions}
              placeholder="Select Approver"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TVOCCertificate;